import { ClockCounterClockwise } from '@phosphor-icons/react';
import { useListFlowRuns } from '@reelplicate/api-client/flow-runs';
import { useGetFlow } from '@reelplicate/api-client/flows';
import type { FlowRunSummaryDto } from '@reelplicate/api-client/model';
import { useListWorkspaces } from '@reelplicate/api-client/workspaces';
import {
  Badge,
  Button,
  DataTable,
  type DataTableColumn,
  EmptyState,
  Skeleton,
  Stack,
} from '@reelplicate/ui';
import { Link as RouterLink, useParams } from 'react-router';

import { useFullBleed } from '@/components/layout-mode';
import { formatWhen } from '@/lib/format-when';

import { FlowPageHeader } from '../flow-page-header';
import { RUN_STATUS, runKind } from '../flow-status';

const TRIGGERS: Record<FlowRunSummaryDto['trigger'], string> = {
  MANUAL: 'Run by hand',
  SCHEDULE: 'Schedule',
  WINNER: 'On Pick winner',
};

/** `/flows/:flowId/runs` - the flow's run history, newest first. */
export function RunsPage() {
  useFullBleed();
  const { flowId = '' } = useParams();
  const { data: workspaces } = useListWorkspaces();
  const workspaceId = workspaces?.[0]?.id ?? '';
  const enabled = { query: { enabled: Boolean(workspaceId) } };
  const { data: flow } = useGetFlow(workspaceId, flowId, enabled);
  const { data: runs } = useListFlowRuns(workspaceId, flowId, enabled);

  const columns: DataTableColumn<FlowRunSummaryDto>[] = [
    {
      id: 'started',
      header: 'Started',
      width: 200,
      cell: (run) => formatWhen(run.startedAt),
    },
    { id: 'kind', header: 'Run', width: 160, cell: (run) => runKind(run) },
    {
      id: 'trigger',
      header: 'Trigger',
      width: 160,
      cell: (run) => TRIGGERS[run.trigger],
    },
    {
      id: 'version',
      header: 'Version',
      width: 100,
      cell: (run) => `v${run.versionNumber}`,
    },
    {
      id: 'status',
      header: 'Status',
      width: 180,
      cell: (run) => (
        <Badge tone={RUN_STATUS[run.status].tone} dot>
          {RUN_STATUS[run.status].label}
        </Badge>
      ),
    },
    {
      id: 'error',
      header: 'Notes',
      cell: (run) => (
        // Cells don't wrap by default; a run's note is worth reading in full.
        <span className="text-text-secondary block min-w-[320px] py-3 break-words whitespace-normal">
          {run.error ?? '—'}
        </span>
      ),
    },
  ];

  return (
    <div className="flex h-full min-h-0 flex-col">
      <FlowPageHeader flowId={flowId} title={flow?.name ?? 'Flow'} view="runs" />
      <div className="bg-bg-canvas min-h-0 flex-1 overflow-y-auto">
        <Stack gap={4} className="mx-auto w-full max-w-[1400px] p-8">
          {!runs ? (
            <Skeleton className="h-40" shape="rectangle" />
          ) : (
            <DataTable
              label="Runs"
              columns={columns}
              rows={runs}
              getRowId={(run) => run.id}
              getRowLabel={(run) => `${runKind(run)} ${formatWhen(run.startedAt)}`}
              empty={
                <EmptyState
                  icon={ClockCounterClockwise}
                  title="No runs yet"
                  description="Test run or run the flow from the canvas."
                  actions={
                    <Button asChild variant="outline">
                      <RouterLink to={`/flows/${flowId}`}>Open canvas</RouterLink>
                    </Button>
                  }
                />
              }
            />
          )}
        </Stack>
      </div>
    </div>
  );
}
